/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import is from 'is_js';

import FlexBox from './FlexBox';
import OrderBook from './OrderBook';
import TableShade from './OrderBooks/TableShade';
import CurrentPrice from './CurrentPrice';
import { Tab } from './MiniSymbolsList';
import { decimalPlaces } from '../utils/number';
import { getLabel } from '../utils/i18n';

const DEFAULT_WIDTH = 278; // ssr width
const DEFAULT_SIDE_HEIGHT = 370;

const ButtonsBar = styled.div`
  background-color: ${({ theme }) => theme.boxheadbg};
  display: flex;
  flex-direction: row-reverse;
  flex: 0 0 auto;
  justify-content: space-between;
  padding: 4px;
  height: 30px;
`;

const ButtonsBarDropdown = Tab.withComponent(styled.select`
  margin: 0;
  padding: 2px 3px !important;
`);

const ButtonsBarDropdownLabel = styled.label`
  line-height: 22px;
  color: ${({ theme }) => theme.fontColor2};
  word-bread: keep-all;
`;

const TableFlexBox = styled(FlexBox)`
  position: relative;
  /* no dashes overflow on pro. */
`;

const OrderBookPanel = styled.ul`
  display: flex;
`;

const OrderBookPanelItem = styled.li`
  display: flex;
  padding: 0 0.15em;
`;

const OrderBookPanelButton = Tab.withComponent(styled.input`
  width: 30px;
  height: 22px;
  margin: 0 !important;
  padding: 0.1em !important;
`);

const mapStateToProps = ({ tickers, depthSocketConnected }, { symbol }) => {
  const { tickSize, minTrade } = tickers[symbol] || {};
  if (!tickSize) return {};
  const maxDecimals = decimalPlaces(tickSize);
  return {
    maxGrouping: maxDecimals,
    minTradeDecimals: decimalPlaces(minTrade),
    depthSocketConnected,
  };
};

type Props = {
  i18n: Object,
  symbol: string,
  both: Array<string>,
  minTradeDecimals: number,
  maxGrouping: number | any,
  depthSocketConnected?: boolean,
  defaultWidth: number,
  defaultHeight: number,
  base: string,
};

type State = {
  defaultGrouping: number | any,
  grouping: number | any,
  expand: string | any,
  symbol: string,
};

export default
@connect(mapStateToProps)
class OrderBooks extends React.Component<Props, State> {
  static defaultProps = {
    i18n: {},
    minTradeDecimals: 8,
    maxGrouping: 8,
    depthSocketConnected: false,
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: DEFAULT_SIDE_HEIGHT,
  };

  state = {
    // eslint-disable-next-line
    defaultGrouping: 8,
    grouping: null,
    expand: null,
    symbol: '',
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (props.symbol !== state.symbol) {
      return { grouping: props.maxGrouping, symbol: props.symbol };
    }
    return null;
  }

  _setGrouping = (ev: Object) => {
    if (!ev || !ev.key) return;
    const value = ev.key;
    const grouping = parseInt(value, 10);
    this.setState({ grouping });
  };

  render() {
    const {
      symbol, minTradeDecimals, i18n, both, defaultWidth, defaultHeight, base,
    } = this.props;
    const { grouping, expand } = this.state;
    // const defaultGrouping = this.props.maxGrouping || this.state.defaultGrouping;
    const defaultGrouping = is.undefined(this.props.maxGrouping)
      ? this.state.defaultGrouping
      : this.props.maxGrouping;
    const activeGrouping = is.number(grouping) ? grouping : defaultGrouping;
    return (
      <FlexBox bg="theme" {...this.props}>
        <ButtonsBar align="center">
          <FlexBox basis="100%" wrap="nowrap" justify="flex-end">
            <ButtonsBarDropdownLabel>
              {getLabel('exchange-trade-groups', i18n)}
            </ButtonsBarDropdownLabel>
            <ButtonsBarDropdown
              onChange={ev => {
                ev.preventDefault();
                this._setGrouping({ key: ev.target.value });
              }}
              defaultValue={activeGrouping}
            >
              {new Array(defaultGrouping + 1)
                .fill(true)
                .slice(-4)
                .map((_, idx) => (
                  <option value={defaultGrouping - idx} key={defaultGrouping - idx}>
                    {defaultGrouping - idx} {getLabel('exchange-trade-decimal', i18n)}
                  </option>
                ))
                .reverse()}
            </ButtonsBarDropdown>
          </FlexBox>
          <OrderBookPanel>
            <OrderBookPanelItem>
              <OrderBookPanelButton
                type="image"
                alt="orderbook-buysell"
                src={require('./OrderBooks/orderbook-buysell.svg')}
                onClick={() => {
                  this.setState({ expand: null });
                }}
                selected={expand === null}
              />
            </OrderBookPanelItem>
            <OrderBookPanelItem>
              <OrderBookPanelButton
                type="image"
                alt="orderbook-buy"
                src={require('./OrderBooks/orderbook-buy.svg')}
                onClick={() => {
                  this.setState({ expand: 'bids' });
                }}
                selected={expand === 'bids'}
              />
            </OrderBookPanelItem>
            <OrderBookPanelItem>
              <OrderBookPanelButton
                type="image"
                alt="orderbook-sell"
                src={require('./OrderBooks/orderbook-sell.svg')}
                onClick={() => {
                  this.setState({ expand: 'asks' });
                }}
                selected={expand === 'asks'}
              />
            </OrderBookPanelItem>
          </OrderBookPanel>
        </ButtonsBar>
        <TableFlexBox flex="1" basis={expand === 'asks' ? '100%' : '2em'}>
          <TableShade hidden={this.props.depthSocketConnected} />
          <OrderBook
            i18n={i18n}
            symbol={symbol}
            both={both}
            expand={expand}
            type="asks"
            grouping={activeGrouping}
            defaultGrouping={defaultGrouping}
            amountDecimals={minTradeDecimals}
            reverse={true}
            scrollToBottom={true}
            groupDecimals={activeGrouping}
            defaultWidth={defaultWidth}
            defaultHeight={defaultHeight}
            scrollable={expand === 'asks'}
          />
        </TableFlexBox>
        <CurrentPrice
          flex="0 0 29px"
          symbol={symbol}
          base={base}
          decimals={defaultGrouping}
          direction="row"
        />
        <TableFlexBox flex="1" basis={expand === 'bids' ? '100%' : ''}>
          <TableShade hidden={this.props.depthSocketConnected} />
          <OrderBook
            i18n={i18n}
            symbol={symbol}
            both={both}
            expand={expand}
            type="bids"
            omitTopHeader={true}
            grouping={activeGrouping}
            defaultGrouping={defaultGrouping}
            amountDecimals={minTradeDecimals}
            defaultWidth={defaultWidth}
            defaultHeight={defaultHeight}
            scrollable={expand === 'bids'}
          />
        </TableFlexBox>
      </FlexBox>
    );
  }
}
